<script setup lang="ts">
import ButtonIcon from '#components/elements/button/buttonIcon/ButtonIcon.vue';
import type { CheckBoxeType } from '#components/elements/checkBox/CheckBoxGroup.vue';
import { useLanguage } from '#composables/useLanguage';
import type { DataTableColumn, DataTableDimensionsType, DataTableViewType } from '@carrefour-gcs/shared';
import { ref, watch } from 'vue';
import { applyCheckboxDependencyRules, dimensionsToCheckboxList } from '../configurationPopIn.helpers';
import SortableCheckBoxList from './SortableCheckBoxList.vue';

const { datatableView, availableDimensions, selectedDimensions, disabledDimensions, dimensionsOrder } = defineProps<{
  datatableView: DataTableViewType;
  availableDimensions: Pick<DataTableColumn, 'name' | 'label' | 'required'>[];
  selectedDimensions: DataTableDimensionsType[];
  disabledDimensions: DataTableDimensionsType[];
  dimensionsOrder: DataTableDimensionsType[];
}>();

const isDimensionsSectionExpanded = ref(true);
const { t } = useLanguage();

const formDimensionsList = defineModel<CheckBoxeType[]>('formDimensionsList', { default: () => [] });

function onDimensionsSelectionChanged(list: CheckBoxeType[]) {
  //Updates checkbox disabled states based on dependency rules in DIMENSION_DEPENDENCY_RULES_PER_DATATABLE_VIEW
  const checkboxesWithUpdatedState = applyCheckboxDependencyRules(datatableView, list);

  formDimensionsList.value = checkboxesWithUpdatedState;
}

function orderDimensions(list: CheckBoxeType[]) {
  formDimensionsList.value = list;
}

watch(
  () => availableDimensions,
  () => {
    formDimensionsList.value = dimensionsToCheckboxList(
      availableDimensions,
      selectedDimensions,
      disabledDimensions,
      dimensionsOrder,
      t,
      formDimensionsList.value
    );
  },
  { immediate: true }
);
</script>

<template>
  <div class="my-4">
    <div class="flex items-center justify-between">
      <h4 class="text-xl text-primary font-bold">
        {{ t('dataTableConfigurationPopIn.dimensions.title') }}
      </h4>

      <ButtonIcon
        variant="primary"
        :icon="isDimensionsSectionExpanded ? 'chevron-up' : 'chevron-down'"
        background="transparent"
        @click="isDimensionsSectionExpanded = !isDimensionsSectionExpanded"
      />
    </div>

    <div v-if="isDimensionsSectionExpanded" class="mt-2">
      <p>{{ t('dataTableConfigurationPopIn.dimensions.subtitle') }}</p>

      <div class="max-h-[400px] py-1 pr-4 mr-4 mt-4 overflow-y-auto custom-vertical-scrollbar">
        <SortableCheckBoxList
          v-model="formDimensionsList"
          @update:model-value="onDimensionsSelectionChanged"
          @update-list="orderDimensions"
        />
      </div>
    </div>
  </div>
</template>
